const API_URL = process.env.REACT_APP_BACKEND_URL;

const VIDEO_EXT_RE = /\.(mp4|webm|mov|m4v|qt)(\?|#|$)/i;
const CAMERA_PREFIX_RE = /^(VID|IMG|DJI|GOPR|GH\d*|PXL|MVI|MOV|DSC|CLIP)[_\- ]?\d+(?:[_\- ]\d+)*[_\- ]*/i;
const FILE_EXT_RE = /\.(mp4|mov|m4v|webm|avi|mkv|qt|jpg|jpeg|png|heic)$/i;

// First non-empty source across every field name our collections use.
export function pickSource(r) {
  if (!r) return '';
  const keys = ['web_url', 'embed_code', 'video_url', 'url', 'final_video_url', 'source_video_url', 'vimeo_url', 'youtube_url'];
  for (const k of keys) {
    const v = r[k];
    if (v && String(v).trim()) return String(v).trim();
  }
  if (r.youtube_video_id) return `https://www.youtube.com/embed/${r.youtube_video_id}`;
  return '';
}

// Rewrites public watch URLs to their embeddable equivalents.
export function toEmbedUrl(src) {
  try {
    const u = new URL(src);
    const host = u.hostname.replace(/^www\./, '');
    if (host === 'player.vimeo.com' || (host.endsWith('youtube.com') && u.pathname.startsWith('/embed/')) || host === 'youtube-nocookie.com') {
      return src;
    }
    if (host === 'vimeo.com') {
      const m = u.pathname.match(/\/(\d{6,})(?:\/([0-9a-f]{6,}))?/);
      if (m) {
        const h = u.searchParams.get('h') || m[2];
        const base = `https://player.vimeo.com/video/${m[1]}`;
        return h ? `${base}?h=${h}` : base;
      }
    }
    if (host === 'youtu.be') {
      const id = u.pathname.replace(/^\//, '').split('/')[0];
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
    if (host.endsWith('youtube.com')) {
      const id = u.searchParams.get('v') || (u.pathname.match(/\/(?:shorts|live)\/([\w-]{11})/) || [])[1];
      if (id) return `https://www.youtube.com/embed/${id}`;
    }
  } catch (_) { /* not a URL */ }
  return src;
}

// Decide how to play a stored source: iframe embed, native <video>, or none.
export function resolvePlayable(input) {
  if (!input) return { kind: 'none' };
  let s = decodeEntities(String(input).trim());
  if (!s) return { kind: 'none' };
  const m = s.match(/src=\s*["']([^"']+)["']/i);
  if (m) {
    const inner = m[1].replace(/&amp;/g, '&');
    if (/<video|<source/i.test(s) || VIDEO_EXT_RE.test(inner)) return { kind: 'video', src: absolutize(inner) };
    return { kind: 'iframe', src: toEmbedUrl(absolutize(inner)) };
  }
  if (s.startsWith('//')) s = `https:${s}`;
  // Relative app paths (e.g. /api/f/<token> share links) are direct files.
  if (s.startsWith('/')) return { kind: 'video', src: absolutize(s) };
  if (/^https?:\/\//i.test(s)) {
    if (/example\.com/i.test(s)) return { kind: 'none' };
    if (VIDEO_EXT_RE.test(s) || /amazonaws\.com|cloudfront\.net|\/api\/f\//i.test(s)) return { kind: 'video', src: s };
    return { kind: 'iframe', src: toEmbedUrl(s) };
  }
  if (/^\d{6,}$/.test(s)) return { kind: 'iframe', src: `https://player.vimeo.com/video/${s}` };
  if (/^[\w-]{11}$/.test(s)) return { kind: 'iframe', src: `https://www.youtube.com/embed/${s}` };
  if (/^(www\.)?(vimeo\.com|youtube\.com|youtu\.be|player\.vimeo\.com)\//i.test(s)) {
    return { kind: 'iframe', src: toEmbedUrl(`https://${s}`) };
  }
  // Any pasted blob that still mentions a Vimeo/YouTube player URL.
  const vimeo = s.match(/(?:player\.)?vimeo\.com\/(?:video\/)?(\d{6,})(?:[^\d][^"'\s]*?[?&]h=([0-9a-f]+))?/i);
  if (vimeo) return { kind: 'iframe', src: `https://player.vimeo.com/video/${vimeo[1]}${vimeo[2] ? `?h=${vimeo[2]}` : ''}` };
  const yt = s.match(/(?:youtube\.com\/(?:embed\/|watch\?v=)|youtu\.be\/)([\w-]{11})/i);
  if (yt) return { kind: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}` };
  return { kind: 'none' };
}

function decodeEntities(s) {
  if (!/&(quot|lt|gt|amp|#3[49]);/i.test(s)) return s;
  return s.replace(/&quot;|&#34;/gi, '"').replace(/&#39;/g, "'").replace(/&lt;/gi, '<').replace(/&gt;/gi, '>').replace(/&amp;/gi, '&');
}

function absolutize(s) {
  return s.startsWith('/') && !s.startsWith('//') ? `${API_URL}${s}` : s;
}

// Human clip name: admin title first, else the filename minus camera noise.
export function clipName(v) {
  if (!v) return '';
  if (v.title && String(v.title).trim()) return String(v.title).trim();
  const raw = String(v.filename || '').trim();
  const cleaned = raw.replace(FILE_EXT_RE, '').replace(CAMERA_PREFIX_RE, '').replace(/[_]+/g, ' ').trim();
  if (!cleaned || /^[\d\s_\-.]+$/.test(cleaned)) return '';
  return cleaned;
}

// "Event name — Video title" (falls back to whichever exists).
export function clipLabel(v, eventTitle) {
  const ev = (eventTitle || v?.event_title || '').trim();
  const name = clipName(v);
  if (ev && name) return `${ev} — ${name}`;
  return name || ev || 'Clip';
}
